/**
 * The desktop app's menu bar (the owner's ask, 15/10; docs/refactor/46): Arquivo, Editar, Exibir, Ir, Executar,
 * Terminal and Ajuda, like an IDE's. Nothing here is new — each item asks for something the app already does, where it
 * already does it: the sidebar opens projects, the Editor opens and saves, the run bar runs (the same events and
 * /commands as its buttons), the prompt box takes a terminal command. Narrow, the menus fold into one "Menu".
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronDown, Menu as MenuIcon } from 'lucide-react'
import { Dropdown, DropdownItem } from './Dropdown'
import { useI18n, LOCALES, LOCALE_LABELS } from '../lib/i18n'
import { openPalette } from '../lib/palette'
import { openNewWindow } from '../lib/appWindows'
import { openFirstRunGuide } from '../lib/firstRunGuide'
import { editorCommand, projectCommand } from '../lib/menuCommands'
import { PROJECT_ACTION_EVENT, PUBLISH_ACTION_EVENT, RUN_ACTION_EVENT } from '../lib/composer'
import { COMPOSE_EVENT, redock, sendCommand } from '../lib/dockPlacement'
import { versionLabel } from '../lib/version'
import { toggleFused } from '../lib/fuse'

interface Item {
  id: string
  label: string
  shortcut?: string
  hint?: string
  disabled?: boolean
  run: () => void
}

interface Menu {
  id: string
  label: string
  items: Array<Item | 'separator'>
}

const EDITOR = '/project/editor'

export function MenuBar({
  settingsItems,
  onToggleNav,
  theme,
  onToggleTheme,
  onOtherAccount,
  compact,
}: {
  settingsItems: Array<{ id: string; label: string; to: string }>
  onToggleNav: () => void
  theme: 'dark' | 'light'
  onToggleTheme: () => void
  onOtherAccount: () => void
  /** Forced for tests; otherwise narrower than 1100 px. */
  compact?: boolean
}) {
  const { t, locale, setLocale } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const nav = useRef<HTMLElement | null>(null)
  const narrow = useNarrow(nav)
  const folded = compact ?? narrow
  const inEditor = location.pathname.startsWith(EDITOR)
  const editing = useLastFocus()

  // Editar: the system's own editing, on whatever had the focus before the menu took it.
  const edit = (command: string) => () => {
    editing.restore()
    document.execCommand(command)
  }
  const fire = (name: string, detail?: string) => () => window.dispatchEvent(new CustomEvent(name, detail === undefined ? undefined : { detail }))

  const menus: Menu[] = [
    {
      id: 'file',
      label: t('menu.file'),
      items: [
        { id: 'new-project', label: t('openProjects.newProject'), run: () => navigate('/start-project') },
        { id: 'open-project', label: t('openProjects.openProject'), run: () => projectCommand('open') },
        { id: 'add-folder', label: t('openProjects.addFolder'), run: () => projectCommand('addFolder') },
        'separator',
        {
          id: 'open-file',
          label: t('menu.openFile'),
          shortcut: 'Ctrl+O',
          run: () => {
            if (!inEditor) navigate(EDITOR)
            editorCommand('open')
          },
        },
        { id: 'save', label: t('common.save'), shortcut: 'Ctrl+S', disabled: !inEditor, hint: inEditor ? undefined : t('menu.needsFile'), run: () => editorCommand('save') },
        { id: 'save-as', label: t('editor.saveAs'), shortcut: 'Ctrl+Shift+S', disabled: !inEditor, hint: inEditor ? undefined : t('menu.needsFile'), run: () => editorCommand('saveAs') },
        'separator',
        { id: 'new-window', label: t('palette.action.newWindow'), run: () => void openNewWindow(null) },
        { id: 'other-account', label: t('otherAccount.menu'), run: onOtherAccount },
        'separator',
        ...settingsItems.map((item) => ({ id: `settings-${item.id}`, label: item.label, run: () => navigate(item.to) })),
        { id: 'account', label: t('settings.account'), run: () => navigate('/settings/account') },
      ],
    },
    {
      id: 'edit',
      label: t('menu.edit'),
      items: [
        { id: 'undo', label: t('menu.undo'), shortcut: 'Ctrl+Z', run: edit('undo') },
        { id: 'redo', label: t('menu.redo'), shortcut: 'Ctrl+Y', run: edit('redo') },
        'separator',
        { id: 'cut', label: t('menu.cut'), shortcut: 'Ctrl+X', run: edit('cut') },
        { id: 'copy', label: t('menu.copy'), shortcut: 'Ctrl+C', run: edit('copy') },
        { id: 'paste', label: t('menu.paste'), shortcut: 'Ctrl+V', run: edit('paste') },
        { id: 'select-all', label: t('menu.selectAll'), shortcut: 'Ctrl+A', run: edit('selectAll') },
        'separator',
        { id: 'find', label: t('menu.find'), shortcut: 'Ctrl+F', disabled: !inEditor, hint: inEditor ? undefined : t('menu.needsFile'), run: () => editorCommand('find') },
      ],
    },
    {
      id: 'view',
      label: t('menu.view'),
      items: [
        { id: 'sidebar', label: t('menu.sidebar'), shortcut: 'Ctrl+B', run: onToggleNav },
        { id: 'prompt', label: t('palette.action.prompt'), shortcut: 'Ctrl+J', run: redock },
        // "Fundir" (docs/refactor/62): only the prompt and its answer, always in front of Windows.
        { id: 'fuse', label: t('palette.action.fuse'), run: () => void toggleFused() },
        'separator',
        { id: 'theme', label: theme === 'dark' ? t('nav.themeLight') : t('nav.themeDark'), run: onToggleTheme },
        ...LOCALES.filter((code) => code !== locale).map((code) => ({ id: `language-${code}`, label: t('palette.action.language', { language: LOCALE_LABELS[code] }), run: () => setLocale(code) })),
      ],
    },
    {
      id: 'go',
      label: t('menu.go'),
      items: [
        { id: 'palette', label: t('palette.button'), shortcut: 'Ctrl+K', run: openPalette },
        'separator',
        { id: 'home', label: t('nav.dashboard'), run: () => navigate('/') },
        { id: 'inbox', label: t('inbox.title'), run: () => navigate('/inbox') },
        { id: 'agents', label: t('nav.area.agents'), run: () => navigate('/agents') },
        { id: 'project', label: t('nav.area.project'), run: () => navigate('/project') },
        { id: 'track', label: t('nav.area.track'), run: () => navigate('/track') },
      ],
    },
    {
      id: 'run',
      label: t('menu.run'),
      items: [
        { id: 'play', label: t('run.play'), run: fire(RUN_ACTION_EVENT) },
        { id: 'publish', label: t('publish.action'), run: fire(PUBLISH_ACTION_EVENT) },
        'separator',
        { id: 'analyze', label: t('project.analyze'), run: fire(PROJECT_ACTION_EVENT, 'analisar') },
        { id: 'launch', label: t('project.launch'), run: fire(PROJECT_ACTION_EVENT, 'o-que-falta') },
        { id: 'pending', label: t('project.pending'), run: fire(PROJECT_ACTION_EVENT, 'pendencias') },
        { id: 'audit', label: t('composer.cmd.audit'), run: () => navigate('/project/quality') },
        'separator',
        { id: 'test', label: t('changes.test'), run: () => sendCommand('/testar') },
        { id: 'commit', label: t('changes.commit'), run: () => sendCommand('/commit') },
      ],
    },
    {
      id: 'terminal',
      label: t('menu.terminal'),
      items: [
        { id: 'command', label: t('menu.newCommand'), hint: t('menu.newCommandHint'), run: () => window.dispatchEvent(new CustomEvent(COMPOSE_EVENT, { detail: '!' })) },
      ],
    },
    {
      id: 'help',
      label: t('nav.help'),
      items: [
        { id: 'guide', label: t('guide.reopen'), run: openFirstRunGuide },
        { id: 'commands', label: t('menu.allCommands'), shortcut: 'Ctrl+K', run: openPalette },
        { id: 'contact', label: t('contact.title'), run: () => navigate('/contact') },
        { id: 'terms', label: t('terms.title'), run: () => navigate('/terms') },
      ],
    },
  ]

  const entries = (items: Menu['items'], close: () => void): ReactNode =>
    items.map((item, index) =>
      item === 'separator' ? (
        <div key={`sep-${index}`} className="dropdown__sep" role="separator" />
      ) : (
        <DropdownItem
          key={item.id}
          label={item.label}
          hint={item.hint}
          side={item.shortcut}
          disabled={item.disabled}
          onSelect={() => {
            close()
            item.run()
          }}
        />
      ),
    )

  if (folded) {
    return (
      <nav ref={nav} className="topbar__menus menu-bar menu-bar--folded" aria-label={t('nav.appMenus')} onMouseDown={editing.keep}>
        <Dropdown label={t('menu.all')} align="start" buttonClassName="btn btn--ghost btn--sm topbar__menu" button={<><MenuIcon size={15} aria-hidden /><span className="topbar__menu-label">{t('menu.all')}</span></>}>
          {(close) =>
            menus.map((menu) => (
              <div key={menu.id} className="menu-bar__section">
                <div className="menu-bar__heading tiny muted">{menu.label}</div>
                {entries(menu.items.filter((item) => item !== 'separator'), close)}
              </div>
            ))
          }
        </Dropdown>
      </nav>
    )
  }

  return (
    <nav ref={nav} className="topbar__menus menu-bar" aria-label={t('nav.appMenus')} onMouseDown={editing.keep}>
      {menus.map((menu) => (
        <Dropdown key={menu.id} label={menu.label} align="start" buttonClassName="btn btn--ghost btn--sm topbar__menu menu-bar__menu" button={<>{menu.label}<ChevronDown size={11} aria-hidden className="topbar__menu-chevron" /></>}>
          {(close) => (
            <>
              {entries(menu.items, close)}
              {menu.id === 'help' && <div className="dropdown__foot tiny muted">{versionLabel()}</div>}
            </>
          )}
        </Dropdown>
      ))}
    </nav>
  )
}

/** The top bar narrower than this: the seven menus fold into one, so the run bar keeps its room. */
const FOLD_BELOW = 1100

/**
 * Whether the top bar is narrow — its own width, not the window's: the prompt box docked at the side takes ~440 px
 * from it (docs/refactor/46).
 */
function useNarrow(nav: { current: HTMLElement | null }): boolean {
  const [narrow, setNarrow] = useState(false)
  useEffect(() => {
    const bar = nav.current?.closest('.topbar') ?? nav.current?.parentElement
    if (!bar || typeof ResizeObserver === 'undefined') return
    const check = () => setNarrow(bar.clientWidth < FOLD_BELOW)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(bar)
    return () => observer.disconnect()
  }, [nav])
  return narrow
}

/**
 * The field that had the focus before the menu was opened, so Editar acts on it: opening a menu moves the focus to its
 * button. Kept on mouse-down on the bar, given back before the command.
 */
function useLastFocus() {
  const last = useRef<HTMLElement | null>(null)
  return {
    keep: () => {
      const active = document.activeElement
      if (active instanceof HTMLElement && !active.closest('.menu-bar') && !active.closest('.dropdown__menu')) last.current = active
    },
    restore: () => last.current?.focus(),
  }
}
